import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { Close } from "@mui/icons-material";
import { TabContext, TabPanel } from "@mui/lab";
import { Avatar, CircularProgress, Drawer, Tab } from "@mui/material";
import ActionBottom from "assets/img/svg/actionBottom";
import ActionDownload from "assets/img/svg/actionDownload";
import ActionSee from "assets/img/svg/actionSee";
import ActionTop from "assets/img/svg/actionTop";
import IsOnline from "assets/img/svg/isOnline";
import { useIsMobile } from "hooks/useIsMobile";
import useWindowSize from "hooks/useWindowSize";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { fetchBeneficiarPersonalInfo } from "store/beneficiaryStore";
import { toggleTitleDrawer } from "store/settingsStore";
import {
    fetchAdvancePaymentsStatement,
    fetchAndSaveAdvanceStatement,
    fetchAndSaveStatement,
    fetchBeneficiarPortalStatement,
    fetchBeneficiaryNetStatement,
    fetchBeneficiaryPrecountStatement,
    fetchBeneficiaryStatement,
    fetchCollectiveManagementStatement,
    fetchContract,
    fetchContractDownload,
    fetchNetStatement,
    fetchPrecountStatement,
} from "store/statementsStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";
import type { IBeneficiariesCompanies, ILastStatement } from "store/types";
import { EPortalType } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import { downloadFile, formatLocaleDateString, getStatementFilename } from "../../../helpers/utils";
import EmptyState from "../EmptyState";
import { Spinner } from "../Spinner";
import StyledButton from "../StyledButton";
import { StyledTabs } from "../StyledTabs";

const statementType = (companyType) => {
    let tabs: any = [];
    switch (companyType) {
        case EPortalType.BOOK:
            tabs = [
                { labelKey: "Summary.StatementOfGrossDuties", type: "gross" },
                { labelKey: "Summary.StatementOfNetDuties", type: "net" },
                { labelKey: "Summary.PrepaymentCertification", type: "precount" },
                { labelKey: "Summary.Contract" },
            ];
            break;
        case EPortalType.AUDIOVISUAL:
        case EPortalType.PRESS:
            tabs = [
                { labelKey: "Summary.StatementOfNetDuties", type: "net" },
                { labelKey: "Summary.PrepaymentCertification", type: "precount" },
                { labelKey: "Summary.Contract" },
            ];
            break;
    }

    return tabs;
};

const buttonStyle = {
    borderRadius: "4px",
    display: "flex",
    gap: "8px",
};

const whiteButtonStyle = {
    ...buttonStyle,
    "&.MuiButton-root": {
        borderWidth: "1px",
    },
};

const blackButtonStyle = {
    ...buttonStyle,
    "&.MuiButton-root": {
        borderWidth: "0",
        backgroundColor: "#2D2D2D",
        color: "#fefefe",
        "&:hover": {
            backgroundColor: "#4D4D4D",
        },
    },
};

interface BPShowStatements {
    toggleDrawer: (anchor: any, open: any) => any;
    setOpen: Dispatch<SetStateAction<boolean>>;
    isOpen: boolean;
    termId?: number | undefined | null;
    periodId?: number | undefined | null;
    beneficiarId: number | undefined | null;
    advancePaymentId?: number | undefined | null;
    articleId?: number | undefined | null;
    term: ILastStatement | null;
    title: string | undefined;
    allStatements?: ILastStatement[];
    statementIndex?: number | null;
    setNewStatement?: any;
    beneficiarCompanies: IBeneficiariesCompanies[];
}

const BPShowStatements = ({
    toggleDrawer,
    setOpen,
    isOpen,
    termId,
    periodId,
    beneficiarId,
    advancePaymentId,
    articleId,
    term,
    title,
    allStatements,
    statementIndex,
    setNewStatement,
    beneficiarCompanies,
}: BPShowStatements) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { isMobile, isPhone } = useIsMobile();
    const navigate = useNavigate();
    const [currentTab, setCurrentTab] = useState(advancePaymentId ? 1 : 0);
    const {
        status,
        beneficiarPortalStatement,
        statementByBeneficiary,
        netStatement,
        netStatementByBeneficiary,
        advancePaymentNetStatement,
        precountStatement,
        precountStatementByBeneficiary,
        advancePaymentPrecountStatement,
        collectiveManagementStatement,
        contract,
        savingStatus,
    } = useSelector((state: RootState) => state.statements);
    const statementContentRef = useRef<any>(null);
    const [pdfHeight, setPdfHeight] = useState<number | null>(null);
    const statementPdfRef = useCallback((node) => {
        if (node !== null) setPdfHeight(node.offsetHeight);
    }, []);
    const windowSize = useWindowSize();
    const [transitionClass, setTransitionClass] = useState<"slide-up-transition" | "slide-down-transition">(
        "slide-up-transition",
    );
    const [numPages, setNumPages] = useState<number>();

    const { user } = useAuthenticator((context) => [context.user]);
    const beneficiaryName = user.attributes?.name || "";

    /** Scale term size for mobile & tablets */
    const scalePDFValue = useMemo(() => {
        const WIDTH_PDF = 914;
        return windowSize.width / WIDTH_PDF;
    }, [windowSize]);

    const muiTabHeight = useMemo(() => {
        if (pdfHeight) {
            const muiTabHeight = pdfHeight * scalePDFValue + 150;
            return muiTabHeight;
        }
    }, [windowSize, currentTab, pdfHeight, scalePDFValue]);
    const { beneficiarPersonalInfo: beneficiary } = useAppSelector((state) => state.beneficiary);

    const company = term ? beneficiarCompanies.find((company) => company.id === term?.companyId) : undefined;

    const { showNet = true, showPrecount = true } = company?.countryFeatures?.statementBlocks ?? {};
    const hasCollectiveManagement = !!term?.hasCollectiveManagement;

    const [isStatementCurrencyNotDefault, setIsStatementCurrencyNotDefault] = useState(
        company && company.currency !== beneficiary.statementCurrency,
    );

    useEffect(() => {
        if (company) {
            dispatch(fetchBeneficiarPersonalInfo({ companyId: company.id }));
        }
    }, [company]);

    const companyTabs = useMemo(() => {
        if (!company) return [];
        return statementType(company.publisherType);
    }, [company]);

    useEffect(() => {
        if (periodId && isOpen && term) {
            if (!beneficiarId && !advancePaymentId) {
                currentTab == 0 &&
                    term.termId &&
                    term.companyId &&
                    dispatch(
                        fetchBeneficiarPortalStatement({
                            termId: termId ?? (term?.id || term?.termId),
                            periodId,
                            companyId: term.companyId,
                        }),
                    );
                currentTab == 1 &&
                    !isStatementCurrencyNotDefault &&
                    dispatch(
                        fetchNetStatement({
                            termId: termId ?? (term?.id || term?.termId),
                            periodId,
                            companyId: term.companyId,
                        }),
                    );
                currentTab == 2 &&
                    !isStatementCurrencyNotDefault &&
                    dispatch(
                        fetchPrecountStatement({
                            termId: termId ?? (term.id || term.termId),
                            periodId,
                            companyId: term.companyId,
                        }),
                    );
                currentTab == 3 &&
                    hasCollectiveManagement &&
                    dispatch(
                        fetchCollectiveManagementStatement({
                            termId: (termId ?? term?.id ?? term?.termId)!,
                            periodId: periodId!,
                            companyId: term.companyId,
                        }),
                    );
            } else if (beneficiarId && !advancePaymentId) {
                // consolidated statement
                currentTab == 0 &&
                    dispatch(
                        fetchBeneficiaryStatement({
                            beneficiaryId: beneficiarId,
                            periodId,
                            companyId: term.companyId,
                        }),
                    );
                currentTab == 1 &&
                    !isStatementCurrencyNotDefault &&
                    dispatch(
                        fetchBeneficiaryNetStatement({
                            beneficiaryId: beneficiarId,
                            periodId,
                            companyId: term.companyId,
                        }),
                    );
                currentTab == 2 &&
                    !isStatementCurrencyNotDefault &&
                    dispatch(
                        fetchBeneficiaryPrecountStatement({
                            beneficiaryId: beneficiarId,
                            periodId,
                            companyId: term.companyId,
                        }),
                    );
            }
        }
        if (isOpen) {
            if (!beneficiarId && advancePaymentId && term) {
                currentTab == 1 &&
                    term.sliceId &&
                    term.termId &&
                    dispatch(
                        fetchAdvancePaymentsStatement({
                            termId: term?.termId,
                            advancePaymentId: term?.id,
                            advancePaymentSliceId: term?.sliceId,
                            statementType: "net",
                            companyId: term?.companyId,
                        }),
                    );
                currentTab == 2 &&
                    term.sliceId &&
                    term.termId &&
                    dispatch(
                        fetchAdvancePaymentsStatement({
                            termId: term?.termId,
                            advancePaymentId: term?.id,
                            advancePaymentSliceId: term?.sliceId,
                            statementType: "precount",
                            companyId: term?.companyId,
                        }),
                    );
            } else if (articleId && !beneficiarId && term) {
                currentTab == 0 && dispatch(fetchNetStatement({ articleId, companyId: term.companyId }));
                currentTab == 1 && dispatch(fetchPrecountStatement({ articleId, companyId: term.companyId }));
                currentTab == 2 &&
                    term.titleId &&
                    dispatch(
                        fetchContract({
                            titleId: term.titleId,
                            articleId,
                            companyId: term.companyId,
                        }),
                    );
            }
        }
    }, [periodId, beneficiarId, advancePaymentId, term, currentTab]);

    useEffect(() => {
        setCurrentTab(advancePaymentId ? 1 : 0);
    }, [isOpen, term?.id]);

    // useEffect(() => {
    //   if (statementIndex > prevStatementIndex) setTransitionClass("slide-down-transition")
    //   if (statementIndex < prevStatementIndex) setTransitionClass("slide-up-transition")
    //   setPrevStatementIndex(statementIndex)
    // }, [statementIndex])

    const savePdf = useCallback(() => {
        if (savingStatus == "pending") return;
        if (!companyTabs[currentTab]) return;
        const consolidatedParam = beneficiarId
            ? { beneficiarId, companyId: term?.companyId }
            : { termId: termId ?? (term?.id || term?.termId) };
        if (periodId) {
            dispatch(
                fetchAndSaveStatement({
                    ...consolidatedParam,
                    periodId,
                    fileName: getStatementFilename(
                        beneficiaryName,
                        title ?? "",
                        t(companyTabs[currentTab].labelKey),
                        new Date(),
                    ),
                    statementType: companyTabs[currentTab].type,
                    companyId: term?.companyId,
                }),
            );
        }
        if (advancePaymentId && term && term.sliceId) {
            dispatch(
                fetchAndSaveAdvanceStatement({
                    termId: term.termId,
                    advancePaymentId: term.id,
                    advancePaymentSliceId: term.sliceId,
                    fileName: getStatementFilename(
                        beneficiaryName,
                        title ?? "",
                        t(companyTabs[currentTab].labelKey),
                        new Date(),
                    ),
                    statementType: companyTabs[currentTab].type,
                    companyId: term.companyId,
                }),
            );
        }
        if (articleId) {
            const filename = getStatementFilename(
                term?.name ?? "",
                beneficiaryName,
                t(companyTabs[currentTab].labelKey),
                new Date(),
            );
            if (currentTab < 2) {
                dispatch(
                    fetchAndSaveStatement({
                        articleId,
                        companyId: term?.companyId,
                        fileName: filename,
                        statementType: companyTabs[currentTab].type,
                        type: "press",
                    }),
                );
            } else if (currentTab == 2 && term?.contractSigned != undefined && term?.contractSigned == 0) {
                term.titleId &&
                    dispatch(
                        fetchContractDownload({
                            titleId: term.titleId,
                            articleId,
                            download: true,
                            filename,
                            companyId: term.companyId,
                        }),
                    );
            } else if (currentTab == 2 && term?.contractSigned != undefined && term?.contractSigned > 0) {
                const linkSource = `data:application/pdf;base64,${contract}`;
                downloadFile({ source: linkSource, fileName: filename, isLink: true });
            }
        }
    }, [dispatch, periodId, savingStatus, currentTab, beneficiarId, advancePaymentId, articleId, term]);

    const handlePreviousStatement = () => {
        if (statementIndex && allStatements) {
            const previousStatement = statementIndex - 1 >= 0 ? statementIndex - 1 : statementIndex;
            setNewStatement(allStatements[previousStatement], previousStatement);
        }
    };

    const handleNextStatement = () => {
        if (typeof statementIndex === "number" && allStatements) {
            const nextStatement = statementIndex + 1 < allStatements.length ? statementIndex + 1 : statementIndex;
            setNewStatement(allStatements[nextStatement], nextStatement);
        }
    };

    const renderStatusChip = (status: number) => {
        if (status === 0) {
            return (
                <div className="bpstatement-period-status closed">
                    <IsOnline fill="#267847" />
                    {t("BPStatement.DefinitiveStatement")}
                </div>
            );
        }
        if (status == 1)
            return (
                <div className="bpstatement-period-status opened">
                    <IsOnline fill="#848B4F" />
                    {t("BPStatement.TemporaryStatement")}
                </div>
            );
        if (status == 2)
            return (
                <div className="bpstatement-period-status pending">
                    <IsOnline fill="#9185CB" />
                    {t("BPStatement.PendingStatement")}
                </div>
            );
        return;
    };

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const onOpenTitle = () => {
        navigate(`/titles/${term?.titleId}`);
        setTimeout(() => {
            dispatch(toggleTitleDrawer(true));
        }, 200);
    };

    return (
        <>
            <Drawer
                anchor={isMobile ? "bottom" : "right"}
                className="show-statement-component"
                open={isOpen}
                onClose={toggleDrawer("right", false)}
            >
                {/* https://blog.openreplay.com/how-to-add-animations-with-react-transition-group/#SwitchTransition%20component */}
                {allStatements && allStatements?.length > 0 && (
                    <div className="show-statement-switch-buttons">
                        <div
                            className={`show-statement-top${
                                typeof statementIndex === "number" && allStatements[statementIndex - 1] == undefined
                                    ? " disabled"
                                    : ""
                            }`}
                            onClick={handlePreviousStatement}
                            onMouseEnter={() => setTransitionClass("slide-up-transition")}
                        >
                            <div className="show-statement-switch-button-title">
                                {typeof statementIndex === "number" &&
                                allStatements[statementIndex - 1] == undefined ? (
                                    ""
                                ) : (
                                    <>
                                        <span>{t("ShowStatements.PreviousStatement")}</span>
                                        <span>
                                            {typeof statementIndex === "number" &&
                                                allStatements[statementIndex - 1]?.name}
                                        </span>
                                    </>
                                )}
                            </div>
                            <ActionTop />
                        </div>
                        <div
                            className={`show-statement-bottom${
                                statementIndex && allStatements[statementIndex + 1] == undefined ? " disabled" : ""
                            }`}
                            onClick={handleNextStatement}
                            onMouseEnter={() => setTransitionClass("slide-down-transition")}
                        >
                            <div className="show-statement-switch-button-title">
                                {statementIndex && allStatements[statementIndex + 1] == undefined ? (
                                    ""
                                ) : (
                                    <>
                                        <span>{t("ShowStatements.NextStatement")}</span>
                                        <span>
                                            {typeof statementIndex === "number" &&
                                                allStatements[statementIndex + 1]?.name}
                                        </span>
                                    </>
                                )}
                            </div>
                            <ActionBottom />
                        </div>
                    </div>
                )}
                <SwitchTransition>
                    <CSSTransition
                        key={statementIndex ?? 0}
                        nodeRef={statementContentRef}
                        classNames={transitionClass}
                        addEndListener={(done: any) =>
                            statementContentRef.current &&
                            statementContentRef.current.addEventListener("transitionend", done, false)
                        }
                    >
                        <div className="show-statements" ref={statementContentRef}>
                            <div className="show-statements-header">
                                <div className="show-statement-title">
                                    <div className="show-statement-title-avatar">
                                        <Avatar
                                            variant="rounded"
                                            src={term?.avatarUrl ?? "#"}
                                            sx={{
                                                width: 42,
                                                height: 42,
                                                backgroundColor: "#FFF2F1",
                                                color: "black",
                                                fontSize: 12,
                                            }}
                                            imgProps={{ sx: { objectFit: "cover" } }}
                                        >
                                            {term?.name?.slice(0, 1).toUpperCase()}
                                        </Avatar>
                                        <span>{term?.name}</span>
                                    </div>
                                    <Close
                                        style={{ color: variables["foreground"], cursor: "pointer" }}
                                        onClick={() => setOpen(false)}
                                    />
                                </div>
                                <div className="show-statement-subtitle">
                                    {term?.periodStart &&
                                        t("ShowStatements.StatementOf", {
                                            from: formatLocaleDateString({ date: term.periodStart }),
                                            to: formatLocaleDateString({ date: term.periodEnd }),
                                        })}
                                    {renderStatusChip(term?.isPeriodActive ?? 0)}
                                </div>
                                {/* <div className="show-statements-goBack" onClick={() => setOpen(false)}><KeyboardArrowLeftIcon />{t("ShowStatements.GoBack")}</div>
                <div className="show-statements-current-statement">{t("ShowStatements.Statement")}</div>
                <div className="show-statements-header-actions"> */}
                                {/* <div className="show-statements-header-button"><Printer /></div> */}
                                {/* <div className="show-statements-header-button" onClick={() => savePdf()}>
                    {savingStatus == "pending" &&
                      <CircularProgress size={20} sx={{color: variables["primary-purple"]}}/>
                    ||
                      <Downloadnew />
                    }
                  </div>
                </div> */}
                            </div>
                            <div className="show-statements-main">
                                <div className="show-statement-actions">
                                    {term?.titleId && (
                                        <StyledButton sx={whiteButtonStyle} onClick={onOpenTitle}>
                                            <ActionSee className="show-statement-actions-svg" />
                                            {t("ShowStatements.SeeTitle")}
                                        </StyledButton>
                                    )}
                                    {/* <StyledButton sx={whiteButtonStyle} onClick={printElem}><ActionPrint className="show-statement-actions-svg" />{t("ShowStatements.Print")}</StyledButton> */}
                                    <StyledButton
                                        sx={blackButtonStyle}
                                        onClick={savePdf}
                                        disabled={savingStatus == "pending"}
                                    >
                                        {savingStatus == "pending" ? (
                                            <CircularProgress size={24} sx={{ color: "#fefefe" }} />
                                        ) : (
                                            <>
                                                <ActionDownload className="show-statement-actions-svg" />
                                                {t("ShowStatements.Download")}
                                            </>
                                        )}
                                    </StyledButton>
                                </div>
                                <TabContext value={currentTab.toString()}>
                                    <StyledTabs
                                        value={+currentTab}
                                        onChange={(_, tab) => setCurrentTab(tab)}
                                        className="show-statements-tab"
                                    >
                                        {!articleId && (
                                            <Tab
                                                label={t("Summary.StatementOfGrossDuties")}
                                                disabled={!!advancePaymentId}
                                            />
                                        )}
                                        {!isStatementCurrencyNotDefault && showNet && (
                                            <Tab label={t("ShowStatements.NetDuties")} />
                                        )}
                                        {!isStatementCurrencyNotDefault && showPrecount && (
                                            <Tab
                                                label={t("ShowStatements.Prepayment")}
                                                disabled={
                                                    term?.precount == "0" ||
                                                    (term?.precount == "2" && (term?.contributions ?? 0) == 0)
                                                }
                                            />
                                        )}
                                        {!articleId && !beneficiarId && hasCollectiveManagement && (
                                            <Tab label={t("Summary.StatementOfCollectiveManagement")} />
                                        )}
                                        {articleId && term?.contractSigned === 2 && (
                                            <Tab label={t("Articles.Contract")} />
                                        )}
                                    </StyledTabs>
                                    {/* <div className="show-statements-buttons-list">
                    {!advancePaymentId && <div className={`show-statements-button${currentTab == 0 && " active" || ""}`} onClick={() => setCurrentTab(0)}>{t("ShowStatements.GrossDuties")}</div>}
                    <div className={`show-statements-button${currentTab == 1 && " active" || ""}`} onClick={() => setCurrentTab(1)}>{t("ShowStatements.NetDuties")}</div>
                    <div className={`show-statements-button${currentTab == 2 && " active" || ""}${term?.precount == "0" ? " disabled" : ""}`} onClick={() => term?.precount == "1" && setCurrentTab(2)}>{t("ShowStatements.Prepayment")}</div>
                  </div> */}
                                    {!articleId && (
                                        <TabPanel sx={isMobile ? { height: `${muiTabHeight}px` } : {}} value="0">
                                            {status === "pending" && (
                                                <div className="spinner-wrapper">
                                                    <Spinner />
                                                    <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                                </div>
                                            )}
                                            {status === "failed" && (
                                                <div className="spinner-wrapper" style={{ height: "unset" }}>
                                                    <EmptyState type="statementFailed" />
                                                </div>
                                            )}
                                            {status === "succeeded" && (
                                                <div
                                                    style={
                                                        isMobile
                                                            ? { transform: `scale(${scalePDFValue})` }
                                                            : { maxWidth: "960px" }
                                                    }
                                                    className="show-pdf-wrapper"
                                                    ref={statementPdfRef}
                                                    dangerouslySetInnerHTML={{
                                                        __html: beneficiarId
                                                            ? statementByBeneficiary
                                                            : beneficiarPortalStatement,
                                                    }}
                                                ></div>
                                            )}
                                            {status === "idle" && (
                                                <div style={{ textAlign: "center" }}>{t("ShowStatements.NoData")}</div>
                                            )}
                                        </TabPanel>
                                    )}
                                    <TabPanel
                                        sx={isMobile ? { height: `${muiTabHeight}px` } : {}}
                                        value={articleId ? "0" : "1"}
                                    >
                                        {status === "pending" && (
                                            <div className="spinner-wrapper">
                                                <Spinner />
                                                <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                            </div>
                                        )}
                                        {status === "failed" && (
                                            <div className="spinner-wrapper" style={{ height: "unset" }}>
                                                <EmptyState type="statementFailed" />
                                            </div>
                                        )}
                                        {status === "succeeded" && (
                                            <div
                                                style={
                                                    isMobile
                                                        ? { transform: `scale(${scalePDFValue})` }
                                                        : { maxWidth: "960px" }
                                                }
                                                className="show-pdf-wrapper"
                                                ref={statementPdfRef}
                                                dangerouslySetInnerHTML={{
                                                    __html: beneficiarId
                                                        ? netStatementByBeneficiary
                                                        : advancePaymentId
                                                          ? advancePaymentNetStatement
                                                          : netStatement,
                                                }}
                                            ></div>
                                        )}
                                        {status === "idle" && (
                                            <div style={{ textAlign: "center" }}>{t("ShowStatements.NoData")}</div>
                                        )}
                                    </TabPanel>
                                    <TabPanel
                                        sx={isMobile ? { height: `${muiTabHeight}px` } : {}}
                                        value={articleId ? "1" : "2"}
                                    >
                                        {status === "pending" && (
                                            <div className="spinner-wrapper">
                                                <Spinner />
                                                <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                            </div>
                                        )}
                                        {status === "failed" && (
                                            <div className="spinner-wrapper" style={{ height: "unset" }}>
                                                <EmptyState type="statementFailed" />
                                            </div>
                                        )}
                                        {status === "succeeded" && (
                                            <div
                                                style={
                                                    isMobile
                                                        ? { transform: `scale(${scalePDFValue})` }
                                                        : { maxWidth: "960px" }
                                                }
                                                className="show-pdf-wrapper"
                                                ref={statementPdfRef}
                                                dangerouslySetInnerHTML={{
                                                    __html: beneficiarId
                                                        ? precountStatementByBeneficiary
                                                        : advancePaymentId
                                                          ? advancePaymentPrecountStatement
                                                          : precountStatement,
                                                }}
                                            ></div>
                                        )}
                                        {status === "idle" && (
                                            <div style={{ textAlign: "center" }}>{t("ShowStatements.NoData")}</div>
                                        )}
                                    </TabPanel>
                                    {!articleId && !beneficiarId && hasCollectiveManagement && (
                                        <TabPanel sx={isMobile ? { height: `${muiTabHeight}px` } : {}} value="3">
                                            {status === "pending" && (
                                                <div className="spinner-wrapper">
                                                    <Spinner />
                                                    <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                                </div>
                                            )}
                                            {status === "failed" && (
                                                <div className="spinner-wrapper" style={{ height: "unset" }}>
                                                    <EmptyState type="statementFailed" />
                                                </div>
                                            )}
                                            {status === "succeeded" && (
                                                <div
                                                    style={
                                                        isMobile
                                                            ? { transform: `scale(${scalePDFValue})` }
                                                            : { maxWidth: "960px" }
                                                    }
                                                    className="show-pdf-wrapper"
                                                    ref={statementPdfRef}
                                                    dangerouslySetInnerHTML={{ __html: collectiveManagementStatement }}
                                                ></div>
                                            )}
                                            {status === "idle" && (
                                                <div style={{ textAlign: "center" }}>{t("ShowStatements.NoData")}</div>
                                            )}
                                        </TabPanel>
                                    )}
                                    {articleId && (
                                        <TabPanel
                                            sx={
                                                isMobile
                                                    ? { height: `${muiTabHeight}px` }
                                                    : { padding: 0, position: "relative", height: "70%" }
                                            }
                                            value="2"
                                        >
                                            {status === "pending" && (
                                                <div className="spinner-wrapper">
                                                    <Spinner />
                                                    <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                                </div>
                                            )}
                                            {status === "failed" && (
                                                <div style={{ textAlign: "center" }}>{t("ShowStatements.Error")}</div>
                                            )}
                                            {status === "succeeded" && (
                                                <>
                                                    {term?.contractSigned && term.contractSigned > 0 ? (
                                                        <Document
                                                            file={`data:application/pdf;base64,${contract}`}
                                                            onLoadSuccess={onDocumentLoadSuccess}
                                                        >
                                                            {Array.apply(null, Array(numPages))
                                                                .map((x, i) => i + 1)
                                                                .map((page) => (
                                                                    <Page
                                                                        renderAnnotationLayer={false}
                                                                        pageNumber={page}
                                                                        scale={1.5}
                                                                        key={`page${page}o${numPages}`}
                                                                    />
                                                                ))}
                                                        </Document>
                                                    ) : (
                                                        <div
                                                            className="article-detail-content-html-contract"
                                                            style={isMobile ? { height: `${muiTabHeight}px` } : {}}
                                                        >
                                                            <div
                                                                style={
                                                                    isMobile
                                                                        ? { transform: `scale(${scalePDFValue})` }
                                                                        : { maxWidth: "960px" }
                                                                }
                                                                ref={statementPdfRef}
                                                                className="show-pdf-wrapper"
                                                                dangerouslySetInnerHTML={{ __html: contract }}
                                                            ></div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </TabPanel>
                                    )}
                                </TabContext>
                            </div>
                        </div>
                    </CSSTransition>
                </SwitchTransition>
            </Drawer>
        </>
    );
};

export default BPShowStatements;
